import React, {useEffect, useState, useCallback, useContext} from 'react';
import {Ad} from 'titan-ads/lib/shapes/TitanShapes';
import {NodeShape} from 'lincd/lib/shapes/SHACL';
import {TitanQueryConfig, loadAdData, isDateToday} from '../utils';
import {Spinner} from '../components/Spinner';
import {InstanceOverview} from '../components/InstanceOverview';
import {debounce} from 'lodash';
import {ThemeContext} from '../contexts/Theme';
import AdActionDropdown from '../components/AdActionDropdown';
import {useCustomColumns} from '../hooks/useCustomColumns';
import { useNavigate } from 'react-router-dom';
import './DayPartingTracker.scss';
import styles from './DayPartingTracker.scss.json';
import DatePickerFilter from '../components/DatePickerFilter';
import {FilterProps, TimeRangeValue, TimeRangeProps} from '../types';
import dayjs from 'dayjs';
import { DashboardContext } from '../contexts/Dashboard';
import { TableColumn } from '../utils/views/TableDataProvider';
import { ROUTES } from '../routes';

interface DynamicObject {
  [key: string]: any;
}

interface DayPartingTracker {
  batchActions?: any; // Adjust the type as needed
}

function DayPartingTracker({batchActions}: DayPartingTracker) {
  const {isLight} = useContext(ThemeContext); // Access the theme context
  const [instances, setInstances] = useState<DynamicObject[]>();
  const [columns, setColumns] = useState<TableColumn<any>[]>();
  const [sorting, setSorting] = React.useState<TitanQueryConfig['sortBy'][]>([
    {
      id: 'clicks',
      desc: true,
    },
  ]);
  const [pagination, _setPagination] = useState({pageIndex: 0, pageSize: 10}); // Pagination state
  const [totalItems, setTotalItems] = useState(0); // Total number of items on the server
  const [searchQuery, setSearchQuery] = useState('');
  const [timeRangeOptions, setTimeRangeOptions] = useState<TimeRangeValue[]>([
      { value: 'yesterday', label: 'Yesterday' },
      { value: 'today', label: 'Today' },
      { value: 'customSingleDate', label: 'Custom single date' },
    ])

  const initialFilter = [
    { field: 'impressions', value: '0', operator: 'greater' }
  ];
  const [filtersData, setFiltersData] = useState<FilterProps[]>(initialFilter);
  // const [filtersData, setFiltersData] = useState([{ field: 'isActive', value: 'Yes', operator: 'equal' }]);

  const [dataLoading, setDataLoading] = useState(true);

  // set default time range to last 7 days
  const [timeRange,setTimeRange] = useState<TimeRangeProps>({
    value: 'today',
    date: dayjs().format('YYYY-MM-DD'),
  });

  const handleTimeRangeChange = (newTimeRange: TimeRangeProps) => {
    //make sure we always do a single date, otherwise it triggers AdOptimisationReport mapping
    if(newTimeRange.startDate) {
      newTimeRange.date = newTimeRange.startDate;
      delete newTimeRange.startDate
      delete newTimeRange.endDate;
    }
    setTimeRange(newTimeRange);
  };

  const navigate = useNavigate();
  const shape: NodeShape = Ad.shape;

  const setPagination = (val) => {
    return _setPagination(val);
  };

  // const debouncedSetSearchQuery = useCallback(
  //   debounce((value) => setSearchQuery(value), 500), // 500ms debounce delay
  //   []
  // );

  const debouncedSetSearchQuery = useCallback(
    debounce((value) => {
      setSearchQuery(value);
      setPagination((prev) => ({...prev, pageIndex: 0})); // Reset to first page
    }, 500), // 500ms debounce delay
    [],
  );

  const handleCheckboxActiveOnly = (checked:boolean) => {
    setFiltersData((prev) => {
      if(!prev) {
        prev = [];
      }
      let updatedFilters: FilterProps[] = [];
      if(checked) {
        // get the filter status `On`, to make sure the filter is not duplicated
        const exists = prev.some(filter => filter.field === 'impressions' && filter.value === '0' && filter.operator === 'greater');
        if (!exists) {
          updatedFilters = [...prev, { field: 'impressions', value: '0', operator: 'greater' }];
        } else {
          updatedFilters = prev;
        }
      } else {
        updatedFilters = prev.filter(filter => filter.field !== 'impressions');
      }
      return updatedFilters;
    });
  };

  const exportToCSVHandler = async () => {
    const exportConfig = {
      searchQuery,                        
      pageSize: pagination.pageSize,      
      pageIndex: pagination.pageIndex,    
      sorting,                            
      filters: filtersData,               
      timeRange,                          
      includeColumns: !columns,           
      exportData: true,                  
    };

    const response = await loadAdData(exportConfig);
    if (response.exportPath) {
      window.location.href = response.exportPath;
    } else {
      alert('No public path returned for the file export');
    }
  };

  async function fetchAdData() {
    try {
      setDataLoading(true);
      const adData = await loadAdData({
        searchQuery: searchQuery,
        pageSize: pagination.pageSize,
        pageIndex: pagination.pageIndex,
        sorting: sorting,
        filters: filtersData,
        timeRange,
        includeColumns:!columns
      });
      setInstances(adData.instances);
      setTotalItems(adData.numInstances);
      setDataLoading(false);
      if(adData.columns) {
        setColumns(adData.columns);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setInstances([]);
    }
  }

  const handleRefreshData = (isRefreshing: boolean, newData: DynamicObject[]) => {
    if (isRefreshing) {
      const updatedInstances = instances.map((instance) => {
        const newInstance = newData.find((data) => data.id === instance.id);
        return newInstance ? newInstance : instance;
      });
      setInstances(updatedInstances);
    }
  };

  useEffect(() => {
      fetchAdData();
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    searchQuery,
    filtersData,
    timeRange,
  ]);

  // const handleFilterInstances = (data: any) => {
  //   if (typeof data === 'boolean' && data) {
  //     fetchAdData();
  //   } else if (data?.numInstances > 0) {
  //     setFilterActive(true);
  //     setInstances(data.instances as DynamicObject[]);
  //     // const totalPages = Math.ceil(data.numInstances / pagination.pageSize);
  //     // if (totalPages < pagination.pageIndex) {
  //     //   _setPagination((prev) => ({...prev, pageIndex: totalPages - 1}));
  //     //   setTotalItems(data.numInstances);
  //     // }
  //   }
  // };

  const customColumns = useCustomColumns(instances, columns, columnConfig);

  const detailsViewHandler = (row) => {
    navigate(`${ROUTES.dayPartingTrackerDetail.path}?uri=${row.id}&date=${timeRange.date}`);
  }

  // trigger the fetchAdData function when the brandAccount changes from quickFilter
  const {curAccount} = useContext(DashboardContext);
  const handleBranchAccountChange = (account: string) => {
    setFiltersData((prev) => {
      if (!prev) {
        prev = [];
      }
      // set the brandAccount filter if the brandAccount option selected
      if (curAccount && curAccount.name !== undefined) {
        // remove the previous brandAccount filter if it exists
        prev = prev.filter(filter => filter.field !== 'brandAccount');
        // add the new brandAccount filter
        return [...prev, { field: 'brandAccount', value: account, operator: 'equal' }];
      } else {
        return prev.filter(filter => filter.field !== 'brandAccount');
      }
    })
  }

  // trigger the fetch the data function when the filter changes
  // newFilters values, e.g. newFilters = [{ field: 'shortAdName', value: 'TF-247', operator: 'equal' }]
  // currentFilter, e.g = [{ field: 'status', value: 'On', operator: 'equal' }]
  // newFilter will replace currentFilter or current filter state
  const handleFilterChange = (newFilters: FilterProps[]) => {
    setFiltersData(newFilters);
  }

  return (
    <>
      {customColumns || instances?.length === 0 ? (
        <InstanceOverview
          instances={instances}
          shape={shape}
          customColumns={customColumns}
          isLoading={dataLoading}
          // defaultColumn={{
          //   meta: {
          //     className: (value,row) => 'status',
          //   }
          // }}
          pagination={pagination}
          resetFilter={(value:boolean) => value && setFiltersData(initialFilter)}
          totalItems={totalItems}
          onRefreshingData={handleRefreshData}
          columnInfo={columns}
          initialFilter={filtersData}
          filterInstances={(filter: FilterProps[]) => handleFilterChange(filter)}
          quickFilter={[
            {
              property: 'impressions',
              label: 'Show only ads with impressions',
              onChange: (checked:boolean) => handleCheckboxActiveOnly(checked),
              value: filtersData?.find(filter => filter.field === 'impressions')?.value === '0',
              defaultValue: '0'
            },
            {
              property: 'brandAccount',
              onChange: (account:string) => handleBranchAccountChange(account),
              value: curAccount?.name,
            }
          ]}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={(...args) => {
            return setSorting(...args);
          }}
          onSearchChange={debouncedSetSearchQuery}
          isLight={isLight}
          triggerActionRefresh={fetchAdData} 
          onDetailsClick={detailsViewHandler}
          batchActionDropdown={(selectedRowData) => (
            <AdActionDropdown
              selectedRowData={selectedRowData}
              // onComplete={fetchAdData}
            />
          )}
          timeRange={timeRange}
          timeRangeOptions={timeRangeOptions}
          onTimeRangeChange={handleTimeRangeChange}
          exportToCSV={exportToCSVHandler}
          // dateFilter={() => (
          //   <DatePickerFilter onDateSelect={setSelectedDate} mode={'multiple'} />
          // )}
        />
      ) : (<Spinner />)}
    </>
  );
}
const columnConfig = {
  //to indicate the column, use the same key here as in the mapping object in views/AdManagement.tsx
  deliveringNow: {
    className: (value, row: DynamicObject) => {
      //you can use the row object if you need to access other values in the row
      if (value === 'No') {
        return styles.lightRed;
      } else if (value === 'Yes') {
        return styles.green;
      }
      return '';
    },
  },
  shortAdName: {
    className: (value, row: DynamicObject) => {
      //you can use the row object if you need to access other values in the row
      const deliveringNow = row.deliveringNow;
      if (deliveringNow === 'No') {
        return styles.lightRed;
      } else if (deliveringNow === 'Yes') {
        return styles.green;
      }
      return '';
    },
  },
  adOptimized: {
    className: (value, row: DynamicObject) => {
      //Show the cell in green if the value is 'Yes'.
      if (value === 'Yes') {
        return styles.green;
      }
      // return styles.lightRed;
    },
  },
  adCPC: {
    className: (value, row: DynamicObject) => {
      //Show the cell as green if the value is lower than adGrossPL value by the calculation of adGrossPL/2.262, show as yellow if the value is lower than adGrossPL value by the calculation of adGrossPL/1.5, and show as red if the value eqiuals higher than adGrossPL
      const adGrossPL = row.adGrossPL;
      if(!value) return;
      if (value < adGrossPL / 2.262) {
        return styles.green;
      } else if (value < adGrossPL / 1.5) {
        return styles.yellow;
      } else if (value >= adGrossPL) {
        return styles.red;
      }
    },
  },
  estimatedAdSpendSinceLastSale: {
    className: (value, row: DynamicObject) => {
      if (typeof row?.sslsPercentageOfPayout === 'string') {
        value = parseFloat(row.sslsPercentageOfPayout.replace(/[,$]/g, ''));
      }
      if (isNaN(value)) return;
      if (value >= 50 && value < 90) {
        return styles.yellow;
      } 
      if (value >= 90) {
        return styles.red;
      }
    },
  },
  sslsPercentageOfPayout: {
    className: (value, row: DynamicObject) => {
      value = parseFloat(row?.sslsPercentageOfPayout.replace(/[,%]/g, ''));
      if (isNaN(value)) return;
      if (value >= 50 && value < 90) {
        return styles.yellow;
      } 
      if (value >= 90) {
        return styles.red;
      }
    },
  },
  //Ad Gross P&L is red if negative, green if positive, and yellow if zero
  adGrossPL: {
    className: (value, row: DynamicObject) => {
      if(!row.adGrossPL) return;
      //get number from the row
      if (typeof row?.adGrossPL === 'string') {
        value = parseFloat(row.adGrossPL.replace(/[,$]/g, ''));
      }
      if (value < 0) {
        return styles.red;
      } else if (value > 0) {
        return styles.green;
      } else if (value === 0) {
        return styles.yellow;
      }
    },
  },
};
export default DayPartingTracker;
